import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MinusCircle } from 'lucide-react';
import { usePersonnalisation } from '../hooks/usePersonnalisation';

/**
 * Ferme l'écran d'un module que l'association a écarté ou n'a pas souscrit.
 *
 * LE DÉFAUT QUE CE COMPOSANT CORRIGE
 * ---------------------------------------------------------------------------
 * Écarter un module depuis Paramètres → Modules retirait son entrée du menu —
 * et c'est tout. L'adresse directe continuait de servir l'écran entier :
 * /admin/boutique restait pleinement utilisable avec l'option refusée, et le
 * bureau concluait, à raison, que la désactivation ne faisait rien.
 *
 * Un module fermé doit se VOIR fermé. La page dit pourquoi elle est close et
 * où la rouvrir, plutôt que de faire semblant d'être un lien mort.
 *
 * CE QUE CE COMPOSANT NE GARDE PAS
 * ---------------------------------------------------------------------------
 * La sécurité — elle est au serveur, sur chaque route d'API, et ne dépend
 * jamais d'un écran. Ici on garde la COHÉRENCE : que l'interrupteur des
 * modules dise la vérité.
 *
 * Tant que la personnalisation n'est pas revenue, on n'efface rien : un écran
 * qui clignote « fermé » une demi-seconde au chargement se remarque bien plus
 * qu'un écran qui attend — même règle que le menu.
 */
export default function GardeOption({
  option,
  nom,
  children,
}: {
  /** La clé dans personnalisation.options : 'boutique', 'licences', 'festival'… */
  option: string;
  /** Le nom du module, tel que l'écran des modules le montre. */
  nom: string;
  children: ReactNode;
}) {
  const perso = usePersonnalisation();
  const ouverte = (perso?.options as Record<string, boolean> | undefined)?.[option] !== false;

  if (ouverte) {
    return children;
  }

  return (
    <div className="p-4 lg:p-0 pb-24 lg:pb-0">
      <div className="card flex items-start gap-3">
        <MinusCircle className="w-5 h-5 mt-0.5 shrink-0 text-gray-400" />
        <div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">
            Le module {nom} est fermé
          </p>
          <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
            Il a été écarté, ou n'est pas souscrit. Ses données sont conservées :
            reprenez-le depuis{' '}
            <Link to="/admin/parametres" className="underline text-primary-700 dark:text-primary-300">
              Paramètres → Modules
            </Link>{' '}
            pour rouvrir cet écran tel que vous l'avez laissé.
          </p>
        </div>
      </div>
    </div>
  );
}
